import { useState } from 'react'
import { SecondaryButton } from '../../../_shared/Buttons/SecondaryButton'
import { TextInput } from '../../../_shared/NumberInput'
import { useMutation, useQueryClient } from 'react-query'
import { Loader } from '../../../_shared/Loader'
import type { SimpleVehicleProps } from '../../../types/types'
import TextArea from '../../../_shared/TextArea'
import TertiaryButton from '../../../_shared/Buttons/TertiaryButton'
import ArrowRight from '../../../icons/ArrowRight'
import { PrimaryButton } from '../../../_shared/Buttons/PrimaryButton'
import { useNavigate } from 'react-router-dom'
import { recordMileage, sendMessageEmail } from '../../../../../../../api/newApi'
import { Cross, Check } from '../../../icons'
import { useChosenCompany, useUser } from '../../../../../../../context/UserProvider'

type EditCarProps = {
  car: SimpleVehicleProps
  setShowEditCar: (show: boolean) => void
}

const EditCar = ({ car, setShowEditCar }: EditCarProps) => {
  const [mileage, setMileage] = useState(car.mileage.toString() || '')
  // const [comment, setComment] = useState('')
  const [message, setMessage] = useState('')
  const queryClient = useQueryClient()

  const { user } = useUser()
  const company = useChosenCompany()

  const carNavigation = !!car.accountNo ? { id: car.accountNo, type: 'account' } : { id: car.budgetNo, type: 'budget' }

  const navigate = useNavigate()

  const sendMessage = useMutation(sendMessageEmail, {
    onSuccess: () => {
      setMessage('')
    },
  })

  const updateMilage = useMutation(
    ['changeMileage', car.accountNo || car.budgetNo],
    () => recordMileage(car.accountNo || car.budgetNo, Number(mileage?.replace(/\D/g, ''))),
    {
      onSuccess: () => {
        queryClient.refetchQueries(['carPark', company?.id])
      },
    },
  )

  if (updateMilage.isError) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-6">
        <p className="text-center max-w-[40ch] leading-7">
          Noe gikk galt under oppdatering. Prøv igjen senere eller kontakt Autoplan.
        </p>
        <SecondaryButton onClick={() => setShowEditCar(false)}>Lukk vinduet</SecondaryButton>
      </div>
    )
  }
  if (updateMilage.isLoading) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-2">
        <Loader />
        <p>Oppdaterer bil...</p>
      </div>
    )
  }

  if (updateMilage.isSuccess) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-6">
        <p className="text-xl">Bilen er oppdatert!</p>
        <SecondaryButton onClick={() => setShowEditCar(false)}>Lukk vinduet</SecondaryButton>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-10 items-start">
      <div className="flex flex-col gap-4">
        <h2 className=" text-2xl ">{car.registrationNumber}</h2>
        <div className="group transition-all">
          <TertiaryButton
            onClick={() => {
              navigate(`/cars/${carNavigation.id}?type=${carNavigation.type}`)
            }}
          >
            <span className="transition-all group-hover:translate-x-1 flex flex-row gap-2">
              <span className="transition-transform">Gå til bilkort</span>
              <ArrowRight />
            </span>
          </TertiaryButton>
        </div>
      </div>
      <div className="w-full flex flex-col gap-4 ">
        <h3 className="text-xl ">Handlinger</h3>
        <div className="w-56">
          <TextInput value={mileage} onChange={setMileage} id="mileage" label="Antall kilometer" suffix="km" />
        </div>
        {/* <TextArea
          value={comment}
          onChange={setComment}
          id={'comment'}
          label={'Valgfri kommentar'}
        /> */}
        <div>
          <PrimaryButton
            onClick={() => {
              updateMilage.mutate()
            }}
          >
            Lagre
          </PrimaryButton>
        </div>
      </div>
      <div className="w-full flex flex-col gap-4 ">
        <h3 className="text-xl ">Andre handlinger</h3>
        <TextArea value={message} onChange={setMessage} id={'message'} label={'Send direktemelding til Autoplan'} />
        {sendMessage.isSuccess && (
          <div className="bg-old-success-light border border-old-success-default rounded-lg p-4 flex items-start gap-3 text-old-success-default">
            <Check />
            <div className="flex-1">
              <h3 className="font-medium ">Melding sendt!</h3>
              <p className="text-sm  mt-1">Din melding har blitt sendt til Autoplan og vil bli behandlet snart.</p>
            </div>
          </div>
        )}
        {sendMessage.isError && (
          <div className="bg-[#ffe1e1] border border-old-content-danger  rounded-lg p-4 flex items-start gap-3 text-old-content-danger">
            <Cross />
            <div className="flex-1">
              <h3 className="font-medium ">Melding avbrutt!</h3>
              <p className="text-sm  mt-1">
                Prøv å send meldingen pånytt, eller kontakt Autoplan hvis problemet vedvarer.
              </p>
            </div>
          </div>
        )}
        <div className="flex h-10 items-center">
          {sendMessage.isLoading ? (
            <div className="flex items-center gap-2">
              <Loader />
              <span>Sender melding...</span>
            </div>
          ) : (
            <SecondaryButton
              disabled={!message.trim()}
              onClick={() => {
                sendMessage.mutate({
                  company: company.name || '',
                  registrationNumber: car.registrationNumber || '',
                  name: user.name || '',
                  mail: user.contactDetails.mail || '',
                  phone: user.contactDetails.phone || '',
                  address: user.contactDetails.address || '',
                  postCode: user.contactDetails.postCode || '',
                  city: user.contactDetails.city || '',
                  message,
                })
              }}
            >
              Send melding
            </SecondaryButton>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditCar
